import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Loader2, RefreshCw, Plus, Pencil, X, Check, Mail, Send, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { spot_number: "", name: "", email: "", phone: "", svemo_ta: "", send_email: false };
const inp = "mt-1.5 bg-black/40 border-white/15";

function BookingForm({ title, value, onChange, spots, currentSpot, onSubmit, onCancel, saving, showEmailToggle }) {
  const opts = [...spots];
  if (currentSpot && !opts.find((s) => s.spot_number === currentSpot)) {
    opts.unshift({ spot_number: currentSpot, area: currentSpot[0], label: "nuvarande" });
  }
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="rounded-xl border border-amber-500/25 bg-[#14161A] p-5 space-y-4" data-testid="booking-form">
      <div className="flex items-center justify-between">
        <h4 className="font-display text-lg font-bold uppercase text-white">{title}</h4>
        {onCancel && <button onClick={onCancel} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-white/70 text-xs uppercase">Depåplats</Label>
          <select value={value.spot_number} onChange={(e) => set("spot_number", e.target.value)} data-testid="booking-form-spot"
            className="mt-1.5 w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none">
            <option value="">Välj plats…</option>
            {opts.map((s) => (
              <option key={s.spot_number} value={s.spot_number}>Depå {s.area} – {s.spot_number}{s.label === "nuvarande" ? " (nuvarande)" : ""}</option>
            ))}
          </select>
        </div>
        <div><Label className="text-white/70 text-xs uppercase">Namn</Label><Input value={value.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="booking-form-name" /></div>
        <div><Label className="text-white/70 text-xs uppercase">E-post</Label><Input type="email" value={value.email} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="booking-form-email" /></div>
        <div><Label className="text-white/70 text-xs uppercase">Telefon</Label><Input value={value.phone} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="booking-form-phone" /></div>
        <div><Label className="text-white/70 text-xs uppercase">Svemo TA</Label><Input value={value.svemo_ta} onChange={(e) => set("svemo_ta", e.target.value)} className={inp} data-testid="booking-form-svemo" /></div>
      </div>
      {showEmailToggle && (
        <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
          <input type="checkbox" checked={!!value.send_email} onChange={(e) => set("send_email", e.target.checked)} data-testid="booking-form-sendmail" className="accent-amber-500" />
          <Mail className="w-4 h-4 text-white/40" /> Skicka bekräftelsemejl till föraren
        </label>
      )}
      <Button onClick={onSubmit} disabled={saving} data-testid="booking-form-submit" className="bg-amber-500 text-black hover:bg-amber-400 font-bold uppercase tracking-widest">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Spara</>}
      </Button>
    </div>
  );
}

export function DepotBookings() {
  const [bookings, setBookings] = useState(null);
  const [spots, setSpots] = useState([]);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [exporting, setExporting] = useState(null);

  const exportList = async (fmt) => {
    setExporting(fmt);
    try {
      const blob = await api.exportDepotBookings(fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `depabokningar.${fmt}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Exporten misslyckades"); }
    finally { setExporting(null); }
  };

  const load = () => {
    api.depotBookings().then(setBookings).catch(() => setBookings([]));
    api.depotSpots().then(setSpots).catch(() => setSpots([]));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Ta bort bokningen och frigöra platsen?")) return;
    try { await api.deleteDepotBooking(id); toast.success("Bokning borttagen – platsen är åter ledig"); }
    catch { toast.error("Kunde inte ta bort bokningen"); }
    load();
  };

  const resend = async (b) => {
    if (!window.confirm(`Skicka bekräftelsemejlet igen till ${b.email}?`)) return;
    setResendingId(b.id);
    try { await api.resendDepotConfirmation(b.id); toast.success(`Bekräftelse skickad till ${b.email}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Mejlet kunde inte skickas"); }
    finally { setResendingId(null); }
  };

  const submitAdd = async () => {
    if (!addForm.spot_number) return toast.error("Välj en depåplats");
    setSaving(true);
    try {
      await api.createManualBooking(addForm);
      toast.success("Bokning tillagd" + (addForm.send_email ? " – mejl skickat" : ""));
      setAdding(false); setAddForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunde inte lägga till bokningen"); }
    finally { setSaving(false); }
  };

  const startEdit = (b) => { setEditId(b.id); setEditForm({ spot_number: b.spot_number, name: b.name, email: b.email, phone: b.phone, svemo_ta: b.svemo_ta }); };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await api.updateDepotBooking(editId, editForm);
      toast.success("Bokning uppdaterad");
      setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunde inte uppdatera bokningen"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="depot-bookings-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-3xl font-black uppercase text-white">Depåbokningar</h2>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-sm text-white/50" data-testid="booking-count">{bookings ? `${bookings.length} bokningar` : "Laddar…"}</span>
          <button onClick={load} className="inline-flex items-center gap-2 text-sm text-amber-500 hover:underline"><RefreshCw className="w-4 h-4" /> Uppdatera</button>
          <Button variant="outline" onClick={() => exportList("xlsx")} disabled={!!exporting} data-testid="export-bookings-xlsx" className="border-white/15 bg-transparent text-white hover:bg-white/10">{exporting === "xlsx" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />} Excel</Button>
          <Button variant="outline" onClick={() => exportList("pdf")} disabled={!!exporting} data-testid="export-bookings-pdf" className="border-white/15 bg-transparent text-white hover:bg-white/10">{exporting === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} PDF</Button>
          {!adding && <Button onClick={() => { setAdding(true); setAddForm(EMPTY); }} data-testid="add-booking-btn" className="bg-amber-500 text-black hover:bg-amber-400"><Plus className="w-4 h-4 mr-1" /> Lägg till manuellt</Button>}
        </div>
      </div>

      {adding && (
        <div className="mb-6">
          <BookingForm title="Ny bokning" value={addForm} onChange={setAddForm} spots={spots}
            onSubmit={submitAdd} onCancel={() => setAdding(false)} saving={saving} showEmailToggle />
        </div>
      )}

      {bookings === null && <div className="grid place-items-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>}
      {bookings && bookings.length === 0 && !adding && <p className="text-white/40 font-mono text-sm">Inga bokningar ännu.</p>}

      <div className="space-y-3">
        {bookings && bookings.map((b) => editId === b.id ? (
          <BookingForm key={b.id} title={`Redigera ${b.spot_number}`} value={editForm} onChange={setEditForm} spots={spots}
            currentSpot={b.spot_number} onSubmit={submitEdit} onCancel={() => setEditId(null)} saving={saving} />
        ) : (
          <div key={b.id} data-testid={`booking-${b.id}`} className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-white/8 bg-[#14161A] p-4">
            <span className="font-display text-3xl font-black text-amber-500 w-16 shrink-0">{b.spot_number}</span>
            <div className="flex-1 min-w-[180px]">
              <div className="font-semibold text-white flex items-center gap-2">{b.name}{b.manual && <span className="font-mono text-[9px] uppercase tracking-wide text-white/40 border border-white/15 rounded px-1.5 py-0.5">Manuell</span>}</div>
              <div className="text-sm text-white/50">{b.email} · {b.phone}</div>
            </div>
            <div className="text-sm"><span className="text-white/50 font-mono text-xs uppercase">Svemo TA </span><span className="text-white">{b.svemo_ta}</span></div>
            <div className="flex items-center gap-3">
              <span className={`font-mono text-[10px] uppercase tracking-wide ${b.email_sent ? "text-emerald-400" : "text-white/40"}`}>{b.email_sent ? "Mail skickat" : "Ej mailat"}</span>
              <span className="font-mono text-[10px] text-white/45">{new Date(b.created_at).toLocaleString("sv-SE")}</span>
              <button onClick={() => resend(b)} disabled={resendingId === b.id} data-testid={`booking-resend-${b.id}`} aria-label="Skicka bekräftelse igen" title="Skicka bekräftelse igen" className="p-2 text-white/40 hover:text-emerald-400 disabled:opacity-50">{resendingId === b.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
              <button onClick={() => startEdit(b)} data-testid={`booking-edit-${b.id}`} aria-label="Redigera bokning" className="p-2 text-white/40 hover:text-amber-500"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(b.id)} data-testid={`booking-delete-${b.id}`} aria-label="Ta bort bokning" className="p-2 text-white/40 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
